import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../../lib/firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowLeft, MapPin, Phone, Clock, Upload, X, Image as ImageIcon, Eye } from 'lucide-react';
import { uploadToR2 } from '../../lib/r2Upload';
import { ImagePreview } from '../../components/ImagePreview';

export default function AdminBranches() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phones: [{ label: 'Əsas', number: '' }],
    workingHours: '',
    description: '',
    mapUrl: '',
    coverImage: '',
    images: []
  });

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    // Real-time listener for branches
    const unsubscribe = onSnapshot(
      collection(db, 'branches'),
      (snapshot) => {
        const branchesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBranches(branchesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching branches:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      phones: [{ label: 'Əsas', number: '' }],
      workingHours: '',
      description: '',
      mapUrl: '',
      coverImage: '',
      images: []
    });
    setEditingBranch(null);
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name || '',
      address: branch.address || '',
      phones: branch.phones && branch.phones.length > 0 
        ? branch.phones 
        : [{ label: 'Əsas', number: branch.phone || '' }],
      workingHours: branch.workingHours || '',
      description: branch.description || '',
      mapUrl: branch.mapUrl || '',
      coverImage: branch.coverImage || '',
      images: branch.images || []
    });
    setDialogOpen(true);
  };

  const handleAddPhone = () => {
    setFormData({
      ...formData,
      phones: [...formData.phones, { label: '', number: '' }]
    });
  };

  const handleRemovePhone = (index) => {
    if (formData.phones.length > 1) {
      setFormData({
        ...formData,
        phones: formData.phones.filter((_, i) => i !== index)
      });
    }
  };

  const handlePhoneChange = (index, field, value) => {
    const newPhones = [...formData.phones];
    newPhones[index][field] = value;
    setFormData({ ...formData, phones: newPhones });
  };

  // Cover image upload
  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingCover(true);
    try {
      const result = await uploadToR2(file, 'branches/covers');
      
      if (result.success) {
        setFormData({ ...formData, coverImage: result.url });
        toast.success('Üz şəkili yükləndi');
      } else {
        toast.error(`Yükləmə xətası: ${result.error}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Şəkil yükləmə xətası');
    } finally {
      setUploadingCover(false);
    }
  };

  // Gallery images upload
  const handleGalleryUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadedUrls = [];

    try {
      for (const file of files) {
        const result = await uploadToR2(file, 'branches/gallery');
        
        if (result.success) {
          uploadedUrls.push(result.url);
        } else {
          toast.error(`Yükləmə xətası: ${result.error}`);
        }
      }

      if (uploadedUrls.length > 0) {
        setFormData({
          ...formData,
          images: [...formData.images, ...uploadedUrls]
        });
        toast.success(`${uploadedUrls.length} şəkil yükləndi`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Şəkil yükləmə xətası');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveGalleryImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitting) return;

    if (!formData.name || !formData.address) {
      toast.error('Ad və ünvan tələb olunur');
      return;
    }

    setSubmitting(true);
    try {
      const validPhones = formData.phones.filter(p => p.number.trim() !== '');

      const branchData = {
        name: formData.name,
        address: formData.address,
        phones: validPhones,
        phone: validPhones.length > 0 ? validPhones[0].number : '',
        workingHours: formData.workingHours,
        description: formData.description,
        mapUrl: formData.mapUrl,
        coverImage: formData.coverImage,
        images: formData.images,
        updatedAt: new Date().toISOString()
      };

      if (editingBranch) {
        await updateDoc(doc(db, 'branches', editingBranch.id), branchData);
        toast.success('Filial yeniləndi');
      } else {
        branchData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'branches'), branchData);
        toast.success('Filial əlavə edildi');
      }

      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving branch:', error);
      toast.error('Xəta baş verdi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (branchId) => {
    if (!window.confirm('Bu filialı silmək istədiyinizə əminsiniz?')) return;

    try {
      await deleteDoc(doc(db, 'branches', branchId));
      toast.success('Filial silindi');
    } catch (error) {
      console.error('Error deleting branch:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        toast.error('Silmə icazəsi yoxdur. Firebase rules-u yoxlayın.');
      } else {
        toast.error(`Xəta: ${error.message}`);
      }
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#D90429] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050000] pt-20" data-testid="admin-branches-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-cinzel text-2xl md:text-3xl font-bold text-white">
                Filiallar
              </h1>
              <p className="text-gray-400 text-sm">{branches.length} filial</p>
            </div>
          </div>

          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#D90429] hover:bg-[#B00220]" data-testid="add-branch-btn">
                <Plus className="w-5 h-5 mr-2" />
                Yeni Filial
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0F0F0F] border-[#333] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cinzel text-xl">
                  {editingBranch ? 'Filialı Redaktə Et' : 'Yeni Filial Əlavə Et'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-400">Filial Adı *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="Nərimanov"
                      data-testid="branch-name-input"
                    />
                  </div>

                  <div>
                    <Label className="text-gray-400">İş Saatları</Label>
                    <Input
                      value={formData.workingHours}
                      onChange={(e) => setFormData({ ...formData, workingHours: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                      placeholder="10:00 - 23:00"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-400">Ünvan *</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="Nərimanov r., Əhmədli küç. 15"
                    data-testid="branch-address-input"
                  />
                </div>

                <div>
                  <Label className="text-gray-400">Google Maps Linki</Label>
                  <Input
                    value={formData.mapUrl}
                    onChange={(e) => setFormData({ ...formData, mapUrl: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="https://maps.app.goo.gl/..."
                  />
                </div>

                {/* Phone Numbers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-gray-400">Əlaqə Nömrələri</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleAddPhone}
                      className="text-[#D90429] hover:text-[#D90429]/80 hover:bg-[#D90429]/10"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Nömrə əlavə et
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {formData.phones.map((phone, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Input
                          value={phone.label}
                          onChange={(e) => handlePhoneChange(index, 'label', e.target.value)}
                          className="bg-[#0A0A0A] border-white/10 text-white w-1/3"
                          placeholder="Etiket (məs: Əsas)"
                        />
                        <Input
                          value={phone.number}
                          onChange={(e) => handlePhoneChange(index, 'number', e.target.value)}
                          className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                          placeholder="+994 50 123 45 67"
                        />
                        {formData.phones.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemovePhone(index)}
                            className="text-gray-400 hover:text-[#D90429]"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label className="text-gray-400">Təsvir</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white mt-1"
                    placeholder="Filial haqqında məlumat..."
                    rows={3}
                  />
                </div>

                {/* Cover Image URL */}
                <div>
                  <Label className="text-gray-400">Üz Şəkili URL (Filiallar siyahısında görünəcək)</Label>
                  <div className="flex gap-3 mt-2 items-start">
                    <Input
                      value={formData.coverImage}
                      onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
                      className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                      placeholder="https://..."
                      data-testid="branch-cover-url"
                    />
                    {formData.coverImage && (
                      <div className="flex items-start gap-2">
                        <ImagePreview url={formData.coverImage} alt="Cover" size="md" />
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, coverImage: '' })}
                          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                          title="Sil"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Gallery URLs */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-gray-400">Qalereya Şəkil URL-ləri</Label>
                    <span className="text-xs text-gray-500">{formData.images.length} şəkil</span>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      id="branch-gallery-input"
                      placeholder="https://... URL yazın və Əlavə et düyməsinə basın (və ya Enter)"
                      className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const v = e.target.value.trim();
                          if (v) {
                            setFormData({ ...formData, images: [...formData.images, v] });
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-white/20 text-white"
                      onClick={() => {
                        const input = document.getElementById('branch-gallery-input');
                        const v = input?.value.trim();
                        if (v) {
                          setFormData({ ...formData, images: [...formData.images, v] });
                          input.value = '';
                        }
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Əlavə et
                    </Button>
                  </div>

                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-2 mt-4">
                      {formData.images.map((url, index) => (
                        <div key={index} className="relative aspect-square">
                          <ImagePreview url={url} alt={`Şəkil ${index + 1}`} size="lg" className="w-full h-full" />
                          <button
                            type="button"
                            onClick={() => handleRemoveGalleryImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 z-10"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-white/20"
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                    disabled={submitting}
                  >
                    Ləğv Et
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#D90429] hover:bg-[#B00220]"
                    data-testid="save-branch-btn"
                    disabled={submitting}
                  >
                    {submitting ? 'Yüklənir...' : (editingBranch ? 'Yenilə' : 'Əlavə Et')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Branches List */}
        {branches.length === 0 ? (
          <div className="text-center py-16">
            <MapPin className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">Hələ filial yoxdur</h3>
            <p className="text-gray-400">İlk filialınızı əlavə edin</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {branches.map((branch, index) => (
              <motion.div
                key={branch.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-[#0F0F0F] border border-white/10 rounded-lg overflow-hidden hover:border-[#D90429]/30 transition-colors"
                data-testid={`admin-branch-${branch.id}`}
              >
                {/* Cover Image */}
                {branch.coverImage ? (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={branch.coverImage}
                      alt={branch.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : branch.images && branch.images.length > 0 ? (
                  <div className="h-40 overflow-hidden">
                    <img
                      src={branch.images[0]}
                      alt={branch.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="h-40 bg-gradient-to-br from-[#D90429]/20 to-[#0A0A0A] flex items-center justify-center">
                    <MapPin className="w-12 h-12 text-[#D90429]/50" />
                  </div>
                )}

                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#D90429]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#D90429]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-cinzel text-lg sm:text-xl font-bold text-white mb-2 break-words">
                          {branch.name}
                        </h3>
                        <div className="space-y-1 text-gray-400 text-xs sm:text-sm">
                          <p className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="break-words">{branch.address}</span>
                          </p>
                          {branch.phones && branch.phones.length > 0 ? (
                            branch.phones.slice(0, 2).map((phone, pIndex) => (
                              <p key={pIndex} className="flex items-center gap-2">
                                <Phone className="w-4 h-4 flex-shrink-0" />
                                <span className="text-gray-500">{phone.label}:</span> {phone.number}
                              </p>
                            ))
                          ) : branch.phone && (
                            <p className="flex items-center gap-2">
                              <Phone className="w-4 h-4 flex-shrink-0" />
                              {branch.phone}
                            </p>
                          )}
                          {branch.workingHours && (
                            <p className="flex items-center gap-2">
                              <Clock className="w-4 h-4 flex-shrink-0" />
                              {branch.workingHours}
                            </p>
                          )}
                          {branch.images && branch.images.length > 0 && (
                            <p className="flex items-center gap-2 text-[#D90429]">
                              <ImageIcon className="w-4 h-4 flex-shrink-0" />
                              {branch.images.length} qalereya şəkili
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2 self-start">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(`/branches/${branch.id}`, '_blank')}
                        className="text-gray-400 hover:text-white hover:bg-white/10"
                        title="Səhifəni göstər"
                      >
                        <Eye className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(branch)}
                        className="text-gray-400 hover:text-white hover:bg-white/10"
                        data-testid={`edit-branch-${branch.id}`}
                      >
                        <Pencil className="w-5 h-5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(branch.id)}
                        className="text-gray-400 hover:text-[#D90429] hover:bg-[#D90429]/10"
                        data-testid={`delete-branch-${branch.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>

                  {branch.description && (
                    <p className="text-gray-500 text-xs sm:text-sm mt-4 sm:ml-16 line-clamp-2 break-words">
                      {branch.description}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
